import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { PHONE_DISPLAY, telLink } from "@/lib/whatsapp";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Fleet", href: "#fleet" },
  { label: "Packages", href: "#packages" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.15 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_2px_20px_rgba(11,77,184,0.06)]" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-8 h-[84px]">
        <a href="/" className="focus:outline-none">
          <Logo variant={scrolled ? "dark" : "light"} size={scrolled ? 54 : 60} />
        </a>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className={`nav-link text-sm font-medium transition-colors ${scrolled ? "text-brand-ink/85 hover:text-brand-blue" : "text-white/90 hover:text-brand-orange"}`}
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={telLink()}
            data-testid="header-call-button"
            className="btn-shine group hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-blue text-white pl-4 pr-5 py-2.5 text-sm font-semibold hover:bg-brand-orange transition-colors"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-white/70"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            <Phone className="w-4 h-4" />
            <span>{PHONE_DISPLAY}</span>
          </a>
          <button
            className="lg:hidden inline-flex items-center justify-center rounded-full w-11 h-11 bg-brand-blue text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-white border-t border-black/5 shadow-xl"
          data-testid="mobile-menu-panel"
        >
          <div className="max-w-[1400px] mx-auto px-6 py-4 grid gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="block py-3 text-base font-medium text-brand-ink hover:text-brand-blue border-b border-black/5"
              >
                {n.label}
              </a>
            ))}
            <a href={telLink()} className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange text-white px-5 py-3 font-semibold">
              <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
